import { X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';
import { useI18n } from '../../i18n/useI18n';
import type { Category, TransactionFrequency, TransactionType, Wallet } from '../../lib/api-types';
import { useCreateTransaction } from '../transactions/transactions.api';

interface AddMovementDialogProps {
  readonly categories: readonly Category[];
  readonly initialType?: TransactionType;
  readonly onClose: () => void;
  readonly onCreated?: (transactionId: string) => void;
  readonly today: string;
  readonly wallets: readonly Wallet[];
}

export function AddMovementDialog({
  categories,
  initialType = 'expense',
  onClose,
  onCreated,
  today,
  wallets,
}: AddMovementDialogProps) {
  const { t } = useI18n();
  const [type, setType] = useState<TransactionType>(initialType);
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState(
    categories.find((category) => initialType === 'transfer' || category.type === initialType)
      ?.id ?? '',
  );
  const [walletId, setWalletId] = useState(wallets[0]?.id ?? '');
  const [toWalletId, setToWalletId] = useState('');
  const [frequency, setFrequency] = useState<TransactionFrequency>('one-time');
  const [date, setDate] = useState(today);
  const [limitDate, setLimitDate] = useState('');
  const [note, setNote] = useState('');
  const mutation = useCreateTransaction();
  const dialogRef = useDialogFocusTrap(onClose);
  const compatibleCategories = categories.filter(
    (category) => type === 'transfer' || category.type === type,
  );
  const canSubmit =
    compatibleCategories.length > 0 &&
    wallets.length > 0 &&
    (type !== 'transfer' || wallets.length > 1);

  const changeType = (nextType: TransactionType) => {
    setType(nextType);
    setCategoryId(
      categories.find((category) => nextType === 'transfer' || category.type === nextType)?.id ??
        '',
    );
    if (nextType !== 'transfer') setToWalletId('');
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const created = await mutation.mutateAsync({
      active: true,
      amount: Number(amount),
      categoryId,
      date,
      frequency,
      limitDate: limitDate || null,
      note,
      toWalletId: type === 'transfer' ? toWalletId : null,
      type,
      walletId,
    });
    onCreated?.(created.id);
    onClose();
  };

  const changeWallet = (nextWalletId: string) => {
    setWalletId(nextWalletId);
    if (nextWalletId === toWalletId) setToWalletId('');
  };

  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        aria-labelledby="add-movement-title"
        aria-modal="true"
        className="movement-dialog"
        ref={dialogRef}
        role="dialog"
      >
        <div className="dialog-heading">
          <div>
            <p className="section-kicker">{t('dashboard.myPlan')}</p>
            <h2 id="add-movement-title">{t('dashboard.addMovement')}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>

        <form className="movement-form" onSubmit={submit}>
          <label>
            {t('dashboard.type')}
            <select
              autoFocus
              value={type}
              onChange={(event) => changeType(event.target.value as TransactionType)}
            >
              <option value="expense">{t('dashboard.expense')}</option>
              <option value="income">{t('dashboard.income')}</option>
              <option value="transfer">{t('dashboard.transfer')}</option>
            </select>
          </label>
          <label>
            {t('dashboard.amount')}
            <input
              min="0.01"
              max="1000000000"
              step="0.01"
              inputMode="decimal"
              required
              type="number"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </label>
          <label>
            {t('dashboard.category')}
            <select
              required
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              {compatibleCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Wallet
            <select
              required
              value={walletId}
              onChange={(event) => changeWallet(event.target.value)}
            >
              {wallets.map((wallet) => (
                <option key={wallet.id} value={wallet.id}>
                  {wallet.name}
                </option>
              ))}
            </select>
          </label>
          {type === 'transfer' && (
            <label>
              {t('dashboard.destinationWallet')}
              <select
                required
                value={toWalletId}
                onChange={(event) => setToWalletId(event.target.value)}
              >
                <option value="">{t('dashboard.selectDestination')}</option>
                {wallets
                  .filter((wallet) => wallet.id !== walletId)
                  .map((wallet) => (
                    <option key={wallet.id} value={wallet.id}>
                      {wallet.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label>
            {t('dashboard.frequency')}
            <select
              value={frequency}
              onChange={(event) => setFrequency(event.target.value as TransactionFrequency)}
            >
              <option value="one-time">{t('dashboard.frequencyOneTime')}</option>
              <option value="weekly">{t('dashboard.frequencyWeekly')}</option>
              <option value="biweekly">{t('dashboard.frequencyBiweekly')}</option>
              <option value="monthly">{t('dashboard.frequencyMonthly')}</option>
            </select>
          </label>
          <label>
            {t('dashboard.date')}
            <input
              required
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </label>
          {frequency !== 'one-time' && (
            <label>
              {t('dashboard.dateLimit')} <span>{t('dashboard.optional')}</span>
              <input
                min={date}
                type="date"
                value={limitDate}
                onChange={(event) => setLimitDate(event.target.value)}
              />
            </label>
          )}
          <label className="field-wide">
            {t('dashboard.note')} <span>{t('dashboard.optional')}</span>
            <textarea
              maxLength={500}
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>

          {!canSubmit && (
            <p className="form-error field-wide" role="alert">
              {t('dashboard.missingData')}
            </p>
          )}
          {mutation.error !== null && (
            <p className="form-error field-wide" role="alert">
              {t('dashboard.saveError')}
            </p>
          )}
          <div className="dialog-actions field-wide">
            <button className="button button-ghost" type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              className="button button-primary"
              disabled={!canSubmit || mutation.isPending}
              type="submit"
            >
              {mutation.isPending ? t('common.saving') : t('dashboard.saveMovement')}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
